import type { JSONSchemaType, ValidateFunction } from 'ajv'
import { ajv, isValidThing } from '../ajv'

export interface DOSAuditReportDeviation {
  deviation: string
  proposedActions: string
  responsible: string
  expirationDate: string | null
  approvalDate: string | null
  deviationOnly: boolean
}

export interface DOSAuditReportCheckPoint {
  id: number
  checkPointNo: string
  translatedDescription: string
  isPointCheckedYes: boolean | null
  notes: string[]
}

export interface DOSAuditReportAuditPoint {
  id: number
  auditPointNo: string
  translatedDescription: string
  note: string | null
  isMandatory: boolean
  isDevelopment: boolean
  isPointCheckedYes: boolean | null
  hasReferenceDocuments: boolean
  deviations: DOSAuditReportDeviation[]
  checkPoints: DOSAuditReportCheckPoint[]
}

export interface DOSAuditReportPledge {
  id: number
  pledgeNo: string
  translatedDescription: string
  note: string | null
  customerPerspective: string
  dealerPerspective: string
  auditPoints: DOSAuditReportAuditPoint[]
}

export interface DOSAuditReportData {
  pledges: DOSAuditReportPledge[]
  dealerName: string
  dealerId: string
  city: string
  certificateValidity: string
  auditDate: string
  auditors: string
  country: string
  certificateExpiryDate: string
  auditNote: string | null
  dosVersion: number
}

export const DOSAuditReportDeviationSchema: JSONSchemaType<DOSAuditReportDeviation> =
  {
    type: 'object',
    properties: {
      deviation: { type: 'string' },
      proposedActions: { type: 'string' },
      responsible: { type: 'string' },
      expirationDate: {
        type: 'string',
        format: 'iso-date-time',
        nullable: true
      },
      approvalDate: { type: 'string', format: 'iso-date-time', nullable: true },
      deviationOnly: { type: 'boolean' }
    },
    required: [
      'deviation',
      'proposedActions',
      'responsible',
      'expirationDate',
      'approvalDate',
      'deviationOnly'
    ]
  }

export const DOSAuditReportCheckPointSchema: JSONSchemaType<DOSAuditReportCheckPoint> =
  {
    type: 'object',
    properties: {
      id: { type: 'integer' },
      checkPointNo: { type: 'string' },
      translatedDescription: { type: 'string' },
      isPointCheckedYes: { type: 'boolean', nullable: true },
      notes: { type: 'array', items: { type: 'string' } }
    },
    required: [
      'id',
      'checkPointNo',
      'translatedDescription',
      'isPointCheckedYes',
      'notes'
    ]
  }

export const DOSAuditReportAuditPointSchema: JSONSchemaType<DOSAuditReportAuditPoint> =
  {
    type: 'object',
    properties: {
      id: { type: 'integer' },
      auditPointNo: { type: 'string' },
      translatedDescription: { type: 'string' },
      note: { type: 'string', nullable: true },
      isMandatory: { type: 'boolean' },
      isDevelopment: { type: 'boolean' },
      isPointCheckedYes: { type: 'boolean', nullable: true },
      hasReferenceDocuments: { type: 'boolean' },
      deviations: {
        type: 'array',
        items: DOSAuditReportDeviationSchema
      },
      checkPoints: {
        type: 'array',
        items: DOSAuditReportCheckPointSchema
      }
    },
    required: [
      'id',
      'auditPointNo',
      'translatedDescription',
      'note',
      'isMandatory',
      'isDevelopment',
      'isPointCheckedYes',
      'deviations',
      'checkPoints'
    ]
  }

export const DOSAuditReportPledgeSchema: JSONSchemaType<DOSAuditReportPledge> =
  {
    type: 'object',
    properties: {
      id: { type: 'integer' },
      pledgeNo: { type: 'string' },
      translatedDescription: { type: 'string' },
      note: { type: 'string', nullable: true },
      customerPerspective: { type: 'string' },
      dealerPerspective: { type: 'string' },
      auditPoints: {
        type: 'array',
        items: DOSAuditReportAuditPointSchema
      }
    },
    required: [
      'id',
      'pledgeNo',
      'translatedDescription',
      'note',
      'customerPerspective',
      'dealerPerspective',
      'auditPoints'
    ]
  }

export const DOSAuditReportDataSchema: JSONSchemaType<DOSAuditReportData> = {
  type: 'object',
  properties: {
    pledges: {
      type: 'array',
      items: DOSAuditReportPledgeSchema
    },
    dealerName: { type: 'string' },
    dealerId: { type: 'string' },
    city: { type: 'string' },
    certificateValidity: { type: 'string' },
    auditDate: { type: 'string' },
    auditors: { type: 'string' },
    country: { type: 'string' },
    certificateExpiryDate: { type: 'string' },
    auditNote: { type: 'string', nullable: true },
    dosVersion: { type: 'integer' }
  },
  required: [
    'pledges',
    'dealerName',
    'dealerId',
    'city',
    'certificateValidity',
    'auditDate',
    'auditors',
    'country',
    'certificateExpiryDate',
    'auditNote'
  ]
}

const validateDOSAuditReportDeviation: ValidateFunction<DOSAuditReportDeviation> =
  ajv.compile(DOSAuditReportDeviationSchema)

export const isValidDOSAuditReportDeviation = (
  data: unknown
): data is DOSAuditReportDeviation =>
  isValidThing<DOSAuditReportDeviation>(data, validateDOSAuditReportDeviation)

const validateDOSAuditReportCheckPoint: ValidateFunction<DOSAuditReportCheckPoint> =
  ajv.compile(DOSAuditReportCheckPointSchema)

export const isValidDOSAuditReportCheckPoint = (
  data: unknown
): data is DOSAuditReportCheckPoint =>
  isValidThing<DOSAuditReportCheckPoint>(data, validateDOSAuditReportCheckPoint)

const validateDOSAuditReportAuditPoint: ValidateFunction<DOSAuditReportAuditPoint> =
  ajv.compile(DOSAuditReportAuditPointSchema)

export const isValidDOSAuditReportAuditPoint = (
  data: unknown
): data is DOSAuditReportAuditPoint =>
  isValidThing<DOSAuditReportAuditPoint>(data, validateDOSAuditReportAuditPoint)

const validateDOSAuditReportPledge: ValidateFunction<DOSAuditReportPledge> =
  ajv.compile(DOSAuditReportPledgeSchema)

export const isValidDOSAuditReportPledge = (
  data: unknown
): data is DOSAuditReportPledge =>
  isValidThing<DOSAuditReportPledge>(data, validateDOSAuditReportPledge)

const validateDOSAuditReportData: ValidateFunction<DOSAuditReportData> =
  ajv.compile(DOSAuditReportDataSchema)

export const isValidDOSAuditReportData = (
  data: unknown
): data is DOSAuditReportData =>
  isValidThing<DOSAuditReportData>(data, validateDOSAuditReportData)
